import {
  ApplicationConfig,
  isDevMode,
  provideBrowserGlobalErrorListeners,
  provideZonelessChangeDetection,
  APP_INITIALIZER,
  PLATFORM_ID,
} from '@angular/core';
import {
  provideRouter,
  withInMemoryScrolling,
  type InMemoryScrollingFeature,
  type InMemoryScrollingOptions,
} from '@angular/router';
import { IMAGE_CONFIG, isPlatformBrowser } from '@angular/common';
import {
  provideClientHydration,
  withEventReplay,
  withNoHttpTransferCache,
} from '@angular/platform-browser';
import { routes } from './app.routes';
import { provideStore } from '@ngrx/store';
import { rootReducer } from './store';
import { AuthenticationEffects } from './store/auth/auth.effects';
import { provideEffects } from '@ngrx/effects';
import { provideStoreDevtools } from '@ngrx/store-devtools';
import {
  HTTP_INTERCEPTORS,
  provideHttpClient,
  withFetch,
  withInterceptors,
  withInterceptorsFromDi,
} from '@angular/common/http';
import { FakeBackendProvider } from './helpers/fake-backend';
import { JwtInterceptor } from './helpers/jwt.interceptor';
import { ErrorInterceptor } from './helpers/error.interceptor';
import { authInterceptor } from './helpers/auth.interceptor';
import { getLoggerInterceptor } from './helpers/logging.interceptor';
import { CountryService } from './core/services/utils/country.service';
import type { CountryLocation } from './core/services/utils/country.service';

import { provideSweetAlert2 } from "@sweetalert2/ngx-sweetalert2";


export const appConfig: ApplicationConfig = {
  providers: [
    provideHttpClient(withInterceptors([authInterceptor, getLoggerInterceptor])),
    provideBrowserGlobalErrorListeners(),
    provideZonelessChangeDetection(),
    provideRouter(routes),
    // Hidratación: el navegador reutiliza el DOM prerenderizado de /home en vez de vaciar
    // <app-root> y redibujar (ese redibujado contaba como un LCP nuevo). Va aquí y no en
    // app.config.server.ts porque este último hace mergeApplicationConfig(appConfig, ...), así
    // que servidor y navegador la reciben (evita NG0505).
    // - withEventReplay: reproduce los clics hechos antes de que Angular termine de hidratar.
    // - withNoHttpTransferCache: el prerender no hace peticiones HTTP que valga la pena
    //   reutilizar (país y monedas solo se piden en el navegador); así no se serializa nada.
    // No se usa withI18nSupport(): el proyecto no tiene atributos i18n en plantillas.
    provideClientHydration(withEventReplay(), withNoHttpTransferCache()),
    provideStore(rootReducer),
    provideSweetAlert2({
      // Optional configuration
      fireOnInit: false,
      dismissOnDestroy: true,
    }),
    provideEffects([AuthenticationEffects]),
    provideStoreDevtools({ maxAge: 25, logOnly: !isDevMode() }),
    {
      provide: APP_INITIALIZER,
      multi: true,
      deps: [CountryService, PLATFORM_ID],
      useFactory: (countryService: CountryService, platformId: object) => () => {
        // En el prerender (Node) no se pide el país: no hay IP de visitante y la petición
        // dejaría el build esperando. Solo el navegador la dispara.
        if (!isPlatformBrowser(platformId)) return;
        // No bloqueante: dispara la petición y deja que el arranque siga. CountryService
        // comparte la misma petición (shareReplay), así que los consumidores la reutilizan.
        countryService.getCountry().subscribe({
          next: (loc: CountryLocation) => {
            console.log('[Startup] GeoIP:', loc.ip, loc.countryCode, loc.country, loc.region, loc.city);
            console.log('[Startup] País objeto completo:', loc);
          },
          error: (err) => console.error('[Startup] Error obteniendo país', err),
        });
      },
    },
  ],
};
